import { useAccount, getIsConnected } from '@puzzlehq/sdk';
import { useAuth } from '@src/data/settings';
import { useState, useEffect } from 'react';

// Module-level singleton to prevent duplicate check attempts across
// multiple hook consumers mounting at the same time.
let checkPromise: Promise<boolean> | null = null;

function checkConnection(): Promise<boolean> {
  if (checkPromise) return checkPromise;

  checkPromise = (async () => {
    try {
      return await getIsConnected();
    } catch {
      return false;
    } finally {
      checkPromise = null;
    }
  })();

  return checkPromise;
}

type EffectiveAccountState = {
  effectiveAddress: string | undefined;
  isReconnecting: boolean;
  isFallback: boolean;
  isDisconnected: boolean;
  hasAuth: boolean;
};

export function useEffectiveAccount(): EffectiveAccountState {
  const { account } = useAccount();
  const persistedAddress = useAuth((state) => state.address);
  const auth = useAuth((state) => state.auth);
  const challengeAuth = useAuth((state) => state.challengeAuth);
  const hasAuth = !!(auth || challengeAuth);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);

  // If SDK account is available, we're connected — no reconnect needed.
  const sdkAddress = account?.address;

  useEffect(() => {
    if (sdkAddress) return; // Already connected, nothing to do.

    let cancelled = false;
    setIsReconnecting(true);

    checkConnection().then((connected) => {
      if (cancelled) return;
      setIsReconnecting(false);
      if (!connected) {
        setReconnectFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [sdkAddress]);

  // If SDK connects at any point (even after reconnect), use that.
  if (sdkAddress) {
    return {
      effectiveAddress: sdkAddress,
      isReconnecting: false,
      isFallback: false,
      isDisconnected: false,
      hasAuth,
    };
  }

  // Still trying to reconnect.
  if (isReconnecting) {
    return {
      effectiveAddress: undefined,
      isReconnecting: true,
      isFallback: false,
      isDisconnected: false,
      hasAuth,
    };
  }

  // Reconnect failed — fall back to persisted address if available.
  if (reconnectFailed && persistedAddress) {
    return {
      effectiveAddress: persistedAddress,
      isReconnecting: false,
      isFallback: true,
      isDisconnected: false,
      hasAuth,
    };
  }

  // No SDK, no persisted address — disconnected.
  return {
    effectiveAddress: undefined,
    isReconnecting: false,
    isFallback: false,
    isDisconnected: true,
    hasAuth,
  };
}
